/**
 * Occupancy Permit Upload Tab Component
 * For uploading and managing center occupancy permit documents
 */
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Upload, FileText, Trash2, Download, Calendar, CheckCircle, AlertCircle, Pencil, Check, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OccupancyPermitTab = ({ centerName = "", organization = {}, onUploadComplete, onComplianceChange }) => {
  const [permitDocuments, setPermitDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingExpiration, setSavingExpiration] = useState(null);
  const [inlineExpiration, setInlineExpiration] = useState({}); // {docId: {expirationDate, expirationNA}}
  const fileInputRef = useRef(null);
  
  // Form state for new upload
  const [uploadData, setUploadData] = useState({
    permitNumber: "",
    issuingAuthority: "",
    issueDate: "",
    expirationDate: "",
    expirationNA: false,
    maxOccupancy: "",
    buildingAddress: ""
  });

  // Ref always holds the latest uploadData — prevents stale closure in uploadFile
  const uploadDataRef = useRef(uploadData);
  uploadDataRef.current = uploadData;

  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    fetchPermitDocuments();
  }, []);

  const fetchPermitDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/occupancy-permit`);
      setPermitDocuments(response.data || []);
    } catch (error) {
      console.error("Error fetching permit documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setUploadData(prev => ({ ...prev, [field]: value }));
  };

  const uploadFile = async (file) => {
    if (!file) return;

    // Always read from ref — guaranteed latest values, no stale closure
    const data = uploadDataRef.current;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF, DOC, DOCX, or image file (JPG, PNG)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('permitNumber', data.permitNumber);
      formData.append('issuingAuthority', data.issuingAuthority);
      formData.append('issueDate', data.issueDate);
      formData.append('expirationDate', data.expirationNA ? "" : data.expirationDate);
      formData.append('expirationNA', data.expirationNA.toString());
      formData.append('maxOccupancy', data.maxOccupancy);
      formData.append('buildingAddress', data.buildingAddress);
      formData.append('centerName', centerName || organization?.name || '');

      const response = await axios.post(`${API}/occupancy-permit/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Occupancy permit uploaded successfully!");
      setPermitDocuments(prev => [response.data, ...prev]);
      if (onUploadComplete) onUploadComplete();
      if (onComplianceChange) onComplianceChange();
      
      // Reset form
      setUploadData({
        permitNumber: "",
        issuingAuthority: "",
        issueDate: "",
        expirationDate: "",
        expirationNA: false,
        maxOccupancy: "",
        buildingAddress: ""
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
      console.error("Error uploading permit document:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/occupancy-permit/${docId}`);
      setPermitDocuments(prev => prev.filter(doc => doc.id !== docId));
      setConfirmDeleteId(null);
      toast.success("Document deleted");
      if (onUploadComplete) onUploadComplete();
      if (onComplianceChange) onComplianceChange();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/occupancy-permit/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName || 'occupancy-permit.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const needsExpiration = (doc) => (!doc.expirationDate && !doc.expirationNA) || isExpired(doc.expirationDate);

  const startInlineExpiration = (doc) => {
    setInlineExpiration(prev => ({
      ...prev,
      [doc.id]: { expirationDate: "", expirationNA: false }
    }));
  };

  const saveExpiration = async (docId) => {
    const vals = inlineExpiration[docId] || {};
    if (!vals.expirationDate && !vals.expirationNA) {
      toast.error("Enter an expiration date or check N/A");
      return;
    }
    setSavingExpiration(docId);
    try {
      await axios.patch(`${API}/occupancy-permit/${docId}/expiration`, {
        expirationDate: vals.expirationNA ? "" : vals.expirationDate,
        expirationNA: vals.expirationNA
      });
      // Update local state immediately
      setPermitDocuments(prev => prev.map(d =>
        d.id === docId
          ? { ...d, expirationDate: vals.expirationNA ? "" : vals.expirationDate, expirationNA: vals.expirationNA }
          : d
      ));
      setInlineExpiration(prev => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
      toast.success("Expiration saved!");
      if (onComplianceChange) onComplianceChange();
    } catch (error) {
      toast.error("Failed to save expiration");
    } finally {
      setSavingExpiration(null);
    }
  };

  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 60 && daysUntilExpiry >= 0;
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Upload Card */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl text-teal-800 flex items-center gap-3">
            <Building2 className="w-7 h-7" />
            Occupancy Permit
          </CardTitle>
          <p className="text-teal-600 text-sm mt-1">Upload and manage center occupancy permit documents</p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Permit Details Form */}
          <div className="bg-white p-4 rounded-lg border border-teal-200">
            <h3 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Permit Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700">Permit Number</Label>
                <Input
                  value={uploadData.permitNumber}
                  onChange={(e) => handleInputChange("permitNumber", e.target.value)}
                  className="mt-1"
                  placeholder="Enter permit number"
                />
              </div>
              <div>
                <Label className="text-gray-700">Issuing Authority</Label>
                <Input
                  value={uploadData.issuingAuthority}
                  onChange={(e) => handleInputChange("issuingAuthority", e.target.value)}
                  className="mt-1"
                  placeholder="e.g., City of..."
                />
              </div>
              <div>
                <Label className="text-gray-700">Maximum Occupancy (optional)</Label>
                <Input
                  value={uploadData.maxOccupancy}
                  onChange={(e) => handleInputChange("maxOccupancy", e.target.value)}
                  className="mt-1"
                  placeholder="e.g., 75 persons"
                />
              </div>
              <div>
                <Label className="text-gray-700">Issue Date</Label>
                <Input
                  type="date"
                  value={uploadData.issueDate}
                  onChange={(e) => handleInputChange("issueDate", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700">Expiration Date</Label>
                <Input
                  type="date"
                  value={uploadData.expirationDate}
                  onChange={(e) => {
                    handleInputChange("expirationDate", e.target.value);
                    if (e.target.value) handleInputChange("expirationNA", false);
                  }}
                  className="mt-1"
                  disabled={uploadData.expirationNA}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    checked={uploadData.expirationNA}
                    onCheckedChange={(checked) => {
                      handleInputChange("expirationNA", checked);
                      if (checked) handleInputChange("expirationDate", "");
                    }}
                  />
                  <Label className="text-sm text-gray-600 cursor-pointer">Never Expires</Label>
                </div>
              </div>
              <div>
                <Label className="text-gray-700">Building Address</Label>
                <Input
                  value={uploadData.buildingAddress}
                  onChange={(e) => handleInputChange("buildingAddress", e.target.value)}
                  className="mt-1"
                  placeholder="Enter building address"
                />
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="bg-white p-4 rounded-lg border border-teal-200">
            <h3 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload Occupancy Permit Document
            </h3>
            <div className="flex items-center gap-4 mb-3">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                disabled={uploading}
                className="flex-1"
              />
            </div>
            
            {/* Drag & Drop Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragOver 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'border-gray-300 bg-gray-50'
              }`}
            >
              <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-teal-600' : 'text-gray-400'}`} />
              <p className={`text-sm ${dragOver ? 'text-teal-600' : 'text-gray-500'}`}>
                {dragOver ? 'Drop file here' : 'Or drag & drop your file here'}
              </p>
            </div>

            {uploading && (
              <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-700 flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                Uploading...
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Documents List */}
      {permitDocuments.length > 0 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-gray-700 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Uploaded Occupancy Permits ({permitDocuments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {permitDocuments.map((doc) => (
                <div 
                  key={doc.id} 
                  className={`p-4 rounded-lg border ${
                    needsExpiration(doc)
                      ? 'bg-amber-50 border-amber-300'
                      : isExpired(doc.expirationDate) 
                        ? 'bg-red-50 border-red-200' 
                        : isExpiringSoon(doc.expirationDate)
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Building2 className={`w-5 h-5 ${
                          needsExpiration(doc)
                            ? 'text-amber-600'
                            : isExpired(doc.expirationDate) 
                              ? 'text-red-600' 
                              : isExpiringSoon(doc.expirationDate)
                                ? 'text-amber-600'
                                : 'text-teal-600'
                        }`} />
                        <div>
                          <span className="font-medium text-gray-800">
                            Occupancy Permit
                          </span>
                          {doc.permitNumber && (
                            <span className="text-sm text-gray-500 ml-2">
                              #{doc.permitNumber}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 mt-1 text-sm">
                        {doc.issuingAuthority && (
                          <span className="text-gray-600">Issued by: {doc.issuingAuthority}</span>
                        )}
                        {doc.maxOccupancy && (
                          <span className="text-gray-600">Max Occupancy: {doc.maxOccupancy}</span>
                        )}
                        {doc.issueDate && (
                          <span className="text-gray-600 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Issued: {new Date(doc.issueDate).toLocaleDateString()}
                          </span>
                        )}
                        {doc.expirationDate && (
                          <span className={`flex items-center gap-1 ${
                            isExpired(doc.expirationDate) 
                              ? 'text-red-600 font-medium' 
                              : isExpiringSoon(doc.expirationDate)
                                ? 'text-amber-600 font-medium'
                                : 'text-gray-600'
                          }`}>
                            {isExpired(doc.expirationDate) && <AlertCircle className="w-3 h-3" />}
                            Expires: {new Date(doc.expirationDate).toLocaleDateString()}
                            {isExpired(doc.expirationDate) && ' (EXPIRED)'}
                            {isExpiringSoon(doc.expirationDate) && ' (Expiring Soon)'}
                          </span>
                        )}
                        {doc.expirationNA && (
                          <span className="text-gray-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Never Expires
                          </span>
                        )}
                      </div>
                      {doc.buildingAddress && (
                        <p className="text-xs text-gray-500 mt-1">
                          Address: {doc.buildingAddress}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDownload(doc)}
                        className="text-teal-600 border-teal-300 hover:bg-teal-50"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                      {confirmDeleteId === doc.id ? (
                        <div className="flex gap-1 items-center">
                          <span className="text-xs text-red-600">Delete?</span>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(doc.id)} className="text-red-600 border-red-400 hover:bg-red-50 px-2">Yes</Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirmDeleteId(null)} className="px-2">No</Button>
                        </div>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setConfirmDeleteId(doc.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Inline expiration prompt for documents missing or expired expiration info */}
                  {needsExpiration(doc) && (
                    <div className={`mt-3 pt-3 border-t ${isExpired(doc.expirationDate) ? 'border-red-200' : 'border-amber-200'}`}>
                      {inlineExpiration[doc.id] !== undefined ? (
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <Label className={`text-xs font-semibold ${isExpired(doc.expirationDate) ? 'text-red-800' : 'text-amber-800'}`}>
                              {isExpired(doc.expirationDate) ? 'New Expiration Date' : 'Expiration Date'}
                            </Label>
                            <Input
                              type="date"
                              value={inlineExpiration[doc.id].expirationDate}
                              onChange={(e) => setInlineExpiration(prev => ({
                                ...prev,
                                [doc.id]: { ...prev[doc.id], expirationDate: e.target.value, expirationNA: false }
                              }))}
                              disabled={inlineExpiration[doc.id].expirationNA}
                              className="mt-1 h-8 text-sm w-44"
                            />
                          </div>
                          <div className="flex items-center gap-2 pb-1">
                            <Checkbox
                              id={`na-${doc.id}`}
                              checked={inlineExpiration[doc.id].expirationNA}
                              onCheckedChange={(checked) => setInlineExpiration(prev => ({
                                ...prev,
                                [doc.id]: { expirationDate: checked ? "" : prev[doc.id].expirationDate, expirationNA: checked }
                              }))}
                            />
                            <Label htmlFor={`na-${doc.id}`} className="text-sm text-gray-700 cursor-pointer">Never Expires</Label>
                          </div>
                          <div className="flex gap-2 pb-1">
                            <Button
                              size="sm"
                              onClick={() => saveExpiration(doc.id)}
                              disabled={savingExpiration === doc.id}
                              className="h-8 bg-teal-600 hover:bg-teal-700 text-white"
                            >
                              {savingExpiration === doc.id ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setInlineExpiration(prev => { const n = {...prev}; delete n[doc.id]; return n; })}
                              className="h-8"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium flex items-center gap-1 ${isExpired(doc.expirationDate) ? 'text-red-700' : 'text-amber-700'}`}>
                            <AlertCircle className="w-4 h-4" />
                            {isExpired(doc.expirationDate)
                              ? 'Permit expired — update expiration date for compliance'
                              : 'Expiration date required for compliance'}
                          </span>
                          <Button
                            size="sm"
                            onClick={() => startInlineExpiration(doc)}
                            className={`h-7 text-white text-xs ${isExpired(doc.expirationDate) ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}
                          >
                            {isExpired(doc.expirationDate) ? 'Renew Permit' : 'Add Expiration'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && permitDocuments.length === 0 && (
        <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-600 text-center mb-2">No occupancy permit uploaded yet</p>
            <p className="text-sm text-gray-500 text-center">
              Fill in the permit information above and upload your occupancy permit document
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OccupancyPermitTab;
